import React, { useState, useCallback, useMemo, ChangeEvent } from 'react';
import { ExpandMore as DownArrow } from '@mui/icons-material';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { Columns, TemplateTypeWithAllTab } from '@/types';
import { PointOfInterestTable } from '../PointOfInterestTable';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  IconButton,
  AccordionDetails,
  TablePagination,
  Checkbox,
} from '@mui/material';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { ALL_POIS_TAB_LABEL } from '@/constants';
import { StyledAccordionSummary } from '../Workpacks/styles';
import { accordionStyles } from '@/components/UserManagement/styles';
import { PointOfInterestTemplateTypeEnum, usePoiWorkflowQuery } from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';
import { WorkFlowStatusChangeDropDown } from './WorkFlowStatusChangeDropDown';
import * as localState from '../../../../modules/pointOfInterest/state';
import { useSetAtom } from 'jotai';
import { abyssColors } from '@/theme/colors';
import { upperSnakeCase } from '@/utils';

type WorkflowProps = {
  template: TemplateTypeWithAllTab;
  selectedPointOfInterests: string[];
  handlePointOfInterestSelect: (selection: string[]) => void;
  clearSelectedPOIs: () => void;
  selectedTab: string;
  columns: Columns[];
  isVerifiedFilters: boolean[];
  isGoverningFilters: boolean[];
  accessibilityFilters: string[];
  statusFilters: string[];
  deckFilters: string[];
  selectedPoiFilters: {
    id: string;
    value: string[];
  }[];
  setSelectedFilterValues: React.Dispatch<React.SetStateAction<SelectedValues>>;
  selectedFilterValues: SelectedValues;
  setSelectedPointOfInterests?: React.Dispatch<React.SetStateAction<string[]>>;
  searchTerm?: string;
  handleResetFilters?: () => void;
  setWorkpackIds: React.Dispatch<React.SetStateAction<string[]>>;
  viewBy: 'poi' | 'workpack' | 'workflow';
  workpackId?: string;
  excludePoisWithWorkpack?: boolean;
  debouncedSearchTerm?: string;
};

export const Workflow = ({
  columns,
  template,
  selectedPointOfInterests,
  handlePointOfInterestSelect,
  clearSelectedPOIs,
  selectedTab,
  isVerifiedFilters,
  isGoverningFilters,
  accessibilityFilters,
  statusFilters,
  deckFilters,
  selectedPoiFilters,
  setSelectedFilterValues,
  selectedFilterValues,
  workpackId,
  excludePoisWithWorkpack,
  debouncedSearchTerm,
  viewBy,
  setWorkpackIds,
}: WorkflowProps) => {
  const structureId = useGetStructureId();
  const setSelectedPointOfInterestId = useSetAtom(localState.selectedPointOfInterestId);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 30 });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [workFlowPOIs, setWorkFlowPOIs] = useState<string[]>([]);

  const type = selectedTab === ALL_POIS_TAB_LABEL ? undefined : upperSnakeCase(template?.key);
  const poiType = template.type;

  const { data: workflowDatas, loading } = usePoiWorkflowQuery({
    variables: {
      input: {
        structureId,
        type,
        selectedPoiFilters,
        isVerified: isVerifiedFilters,
        includeManuallyCreated: true,
        isGoverning: isGoverningFilters,
        accessibility: accessibilityFilters,
        status: statusFilters,
        workpackId,
        excludePoisWithWorkpack,
        searchTerm: debouncedSearchTerm,
      },
      pageInfo: {
        skip: pagination.page * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortBy: 'createdAt',
        sortDir: 1,
      },
    },
    fetchPolicy: 'no-cache',
    skip:
      poiType !== PointOfInterestTemplateTypeEnum.VolumeOfInterest &&
      poiType !== PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest,
  });

  const { workFlows, totalCount } = useMemo(() => {
    const { pointOfInterests, pointOfInterestsCount } = workflowDatas ?? {};
    return {
      workFlows: pointOfInterests,
      totalCount: pointOfInterestsCount?.count || 100,
    };
  }, [workflowDatas]);

  const handleExpandClick = useCallback(
    (id: string) => {
      const selectedWorkflow = workFlows?.find((poi) => poi.id === id);
      setExpandedRow(expandedRow === id ? null : id);
      setWorkFlowPOIs(selectedWorkflow?.linkedPointOfInterestIds || []);
      setSelectedPointOfInterestId(id);
    },
    [expandedRow, setSelectedPointOfInterestId, workFlows]
  );

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = +event.target.value;
    setPagination({ page: 0, rowsPerPage: newRowsPerPage });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPagination((previous) => ({ ...previous, page: newPage }));
  };

  if (loading) {
    return (
      <Box sx={{ mt: 25 }}>
        <PageLoader />
      </Box>
    );
  }

  if (!workFlows || workFlows?.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 10 }}>
        <FeedbackCircle text="No data found" />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '5%' }}></TableCell>
            <TableCell>POI Count</TableCell>
            <TableCell>Saved blocks</TableCell>
            <TableCell>Scaffolding/spider</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workFlows?.map((workflow) => {
            const isOpen = expandedRow === workflow.id;
            const linkedPois = workflow.linkedPointOfInterestIds || [];
            const hasLinkedPois = linkedPois.length > 0;

            return (
              <React.Fragment key={workflow.id}>
                <TableRow
                  sx={{
                    backgroundColor: isOpen ? abyssColors.primary[100] : 'transparent',
                  }}
                  onClick={() => {
                    if (expandedRow === workflow.id) {
                      setSelectedPointOfInterestId(workflow.id);
                    } else {
                      handleExpandClick(workflow.id);
                    }
                  }}
                  hover
                >
                  <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleExpandClick(workflow.id)}
                      sx={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                      data-testid="expand-row-button"
                    >
                      <DownArrow />
                    </IconButton>
                    <Checkbox
                      checked={selectedPointOfInterests.includes(workflow.id)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          handlePointOfInterestSelect([...selectedPointOfInterests, workflow.id]);
                        } else {
                          handlePointOfInterestSelect(
                            selectedPointOfInterests.filter((id) => id !== workflow.id)
                          );
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>{workflow.linkedPointOfInterestIds?.length || 0}</TableCell>
                  <TableCell>
                    {workflow.name + (workflow.poiId ? ` ${workflow.poiId}` : '')}
                  </TableCell>
                  <TableCell>{workflow.accessibility || 'N/A'}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <WorkFlowStatusChangeDropDown
                      pointOfInterestId={workflow.id}
                      status={workflow.status}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0, borderBottom: 'none' }}>
                    <Accordion
                      expanded={isOpen}
                      sx={{
                        ...accordionStyles.root,
                      }}
                    >
                      <StyledAccordionSummary sx={{ display: 'none' }} />
                      <AccordionDetails>
                        {hasLinkedPois && workFlowPOIs.length > 0 ? (
                          <Box sx={{ pt: 1, width: '100%' }}>
                            <PointOfInterestTable
                              poiIds={workFlowPOIs}
                              template={template}
                              selectedPointOfInterests={selectedPointOfInterests}
                              handlePointOfInterestSelect={handlePointOfInterestSelect}
                              clearSelectedPOIs={clearSelectedPOIs}
                              selectedTab={selectedTab}
                              setSelectedFilterValues={setSelectedFilterValues}
                              selectedFilterValues={selectedFilterValues}
                              isVerifiedFilters={isVerifiedFilters}
                              isGoverningFilters={isGoverningFilters}
                              accessibilityFilters={accessibilityFilters}
                              statusFilters={statusFilters}
                              selectedPoiFilters={selectedPoiFilters}
                              columns={columns}
                              viewBy={viewBy}
                              setWorkpackIds={setWorkpackIds}
                              workpackIds={[]}
                              deckFilters={deckFilters}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ pl: 6, py: 2 }}>
                            <strong>No linked POIs</strong>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount || 0}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </TableContainer>
  );
};
